import { FileInputButton } from '@/components/FileInput';
import { ImageAdder } from '@/components/ImageAdder';
import { ImageCropComponent } from '@/components/images/ImageCropComponent';
import { AppContext } from '@/contexts/app.context';
import EquipmentAutocomplete from '@/screens/equipment/EquipmentAutocomplete';
import { exerciseDetailsPaperListStyle } from '@/styles';
import { isAdminUser } from '@/utils/user';
import { ContentCopy, Photo } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Button, Chip, FormControl, FormControlLabel, FormGroup, IconButton, Paper, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { PropsWithChildren, useContext, useState } from 'react';
import { ExercisePrerequisiteDTO, SupportExerciseDTO } from '@/generated';
import ExerciseCategoryAutocomplete from '../../exercise-categories/ExerciseCategoryAutocomplete';
import { ExerciseConfigComponent } from '../elements/ExerciseConfigComponent';
import { ExerciseFactsComponent } from '../elements/ExerciseFactsComponent';
import { ExerciseSynonymInput } from '../elements/ExerciseSynonymComponent';
import { ExternalLinksComponent } from '../elements/ExternalLinksComponent';
import { PaperSubheader } from '../elements/PaperSubheader';
import { SimpleTagConfiguration } from '../elements/SimpleTagConfiguration';
import { TagConfiguration } from '../elements/TagConfiguration';

import { ExerciseDraft, useExerciseEditor } from '../useExerciseEditor';
import { useAllExercisesQuery } from '@/hooks/query-hooks';

export type EditorDialog =
  | null
  | 'textScan'
  | 'progression'
  | 'regression'
  | 'support'
  | 'copySupport'
  | 'copyMuscles'
  | 'copyEquipment'
  | 'copyCategories'
  | 'copyFacts'
  | 'copyTraining'
  | 'copyLinks'
  | 'copyAll'
  | 'supportDetails'
  | 'prerequisite'
  | 'copyPrerequisites'
  | 'prerequisiteDetails'
  | 'discard';

interface SectionProps {
  editor: ReturnType<typeof useExerciseEditor>;
  exercise: ExerciseDraft;
}

export const ExerciseMediaSection = (props: SectionProps) => {
  const { updateExercise, imageFile, setImageFile } = props.editor;
  const { exercise } = props;

  return (
    <ExerciseEditSection title="Image and video">
      <Box sx={{ marginTop: 2, marginBottom: 2 }}>
        <ImageCropComponent imageFile={imageFile} onImageFileChanged={setImageFile} imageId={exercise.imageId} />
      </Box>
      <Box sx={{ mt: 0, mb: 3, gap: 1, flexWrap: 'wrap', display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
        <FileInputButton accept=".png,.jpg,.jpeg" startIcon={<Photo />} onFilePicked={(f) => setImageFile(f)}>
          Change Image
        </FileInputButton>
      </Box>
      <Box>
        <TextField
          placeholder="YouTube Video ID"
          label="YouTube Video ID"
          aria-label="YouTube Video ID"
          fullWidth
          value={exercise.youtubeVideoId ?? ''}
          onChange={(e: any) => {
            if (exercise) updateExercise({ youtubeVideoId: e.target.value });
          }}
        />
      </Box>
    </ExerciseEditSection>
  );
};

export const ExerciseMusclesSection = (props: SectionProps & { openDialog: (dialog: EditorDialog) => void }) => {
  const { updateExercise } = props.editor;
  const { exercise } = props;

  const [muscleSelectionEnabled, setMuscleSelectionEnabled] = useState(false);
  return (
    <ExerciseEditSection title="Muscles & body zone">
      <Stack>
        <Stack direction={'row'} alignItems="center" justifyContent="space-between">
          <FormControl sx={{}} component="fieldset" variant="standard">
            <FormGroup row>
              <FormControlLabel
                sx={{ my: 1 }}
                control={
                  <Switch
                    checked={muscleSelectionEnabled}
                    onChange={(e) => {
                      setMuscleSelectionEnabled(!muscleSelectionEnabled);
                    }}
                    name="simpleTag"
                  />
                }
                label="Specify muscles"
              />
            </FormGroup>
          </FormControl>
          <Tooltip title="Copy muscles and body zone from a progression or regression">
            <IconButton aria-label="Copy muscles and body zone" sx={{ color: 'common.white' }} onClick={() => props.openDialog('copyMuscles')}>
              <ContentCopy />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction={'row'}>
          {!muscleSelectionEnabled ? (
            <SimpleTagConfiguration exercise={exercise} updateExercise={updateExercise} />
          ) : (
            <TagConfiguration exercise={exercise} updateExericse={updateExercise} />
          )}
        </Stack>
      </Stack>
    </ExerciseEditSection>
  );
};

export const ExerciseTitleSection = (props: SectionProps) => {
  const { updateExercise } = props.editor;
  const { exercise } = props;

  return (
    <ExerciseEditSection title="Title / Synonyms">
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2, alignItems: 'start' }}>
        <TextField
          value={exercise?.name}
          placeholder="Title"
          label="Title"
          aria-label="Title"
          fullWidth
          onChange={(e: any) => {
            if (exercise) updateExercise({ name: e.target.value });
          }}
        />
        <ExerciseSynonymInput synonyms={exercise.synonyms ?? []} setExerciseSynonyms={(syn) => updateExercise({ synonyms: syn })} />
      </Box>
    </ExerciseEditSection>
  );
};

export const ExerciseDescriptionSection = (props: SectionProps & { openDialog: (dialog: EditorDialog) => void }) => {
  const { updateExercise } = props.editor;
  const { exercise, openDialog } = props;

  return (
    <ExerciseEditSection title="Description">
      <Stack spacing={2}>
        <TextField
          label={'Description'}
          multiline
          minRows={5}
          maxRows={12}
          fullWidth
          value={exercise?.description}
          onChange={(e: any) => {
            if (exercise) updateExercise({ description: e.target.value });
          }}
        />
        <Button sx={{ alignSelf: 'flex-start' }} onClick={() => openDialog('textScan')}>
          Scan Text
        </Button>
      </Stack>
    </ExerciseEditSection>
  );
};

export const ExerciseTrainingSection = (props: SectionProps & { openDialog: (dialog: EditorDialog) => void }) => {
  const { updateExercise } = props.editor;
  const { exercise } = props;

  return (
    <ExerciseEditSection title="Training settings">
      <ExerciseConfigComponent
        exercise={exercise}
        exerciseConfig={exercise.config}
        onCopy={() => props.openDialog('copyTraining')}
        updateExerciseConfig={(config) => {
          updateExercise({ config: config });
        }}
      />
    </ExerciseEditSection>
  );
};

export const ExerciseLinksSection = (props: SectionProps & { openDialog: (dialog: EditorDialog) => void }) => {
  const { updateExercise } = props.editor;
  const { exercise } = props;
  return (
    <ExerciseEditSection title="External Links">
      <ExternalLinksComponent
        hideTitle
        objectWithLinks={exercise}
        setObjectsWithLinks={updateExercise}
        copyButton={
          <Tooltip title="Copy links from a progression or regression">
            <IconButton aria-label="Copy links" sx={{ color: 'common.white' }} onClick={() => props.openDialog('copyLinks')}>
              <ContentCopy />
            </IconButton>
          </Tooltip>
        }
      />
    </ExerciseEditSection>
  );
};

export const ExerciseFactsSection = (props: SectionProps & { openDialog: (dialog: EditorDialog) => void }) => {
  const { updateExercise } = props.editor;
  const { exercise } = props;
  return (
    <ExerciseEditSection title="Exercise Facts">
      <ExerciseFactsComponent exercise={exercise} updateExercise={updateExercise} onCopy={() => props.openDialog('copyFacts')} />
    </ExerciseEditSection>
  );
};

export const ExerciseImagesSection = (props: SectionProps) => {
  const { updateExercise, setAdditionalImageFiles } = props.editor;
  const { exercise } = props;

  return (
    <ExerciseEditSection title="Additional images">
      <Box>
        <ImageAdder
          imageFileIds={exercise.additionalImageIds ?? []}
          filesUpdated={(files) => {
            setAdditionalImageFiles(files);
          }}
          fileIdsUpdated={(ids) => {
            updateExercise({ additionalImageIds: ids });
          }}
        />
      </Box>
    </ExerciseEditSection>
  );
};

export const ExerciseInfoSection = (props: SectionProps & { openDialog: (dialog: EditorDialog) => void }) => {
  const { updateExercise } = props.editor;
  const { exercise } = props;
  const { state } = useContext(AppContext);
  const user = state?.userProfile;
  const isUniLateral = exercise.isUniLateral ?? false;
  return (
    <ExerciseEditSection title="Info">
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" useFlexGap flexWrap="wrap">
            {isAdminUser(user) && (
              <FormControlLabel
                control={<Switch checked={exercise.publiclyVisible ?? false} onChange={(event) => updateExercise({ publiclyVisible: event.target.checked })} name="public" />}
                label="Publicly visible"
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={isUniLateral}
                  onChange={(event) => updateExercise({
                    isUniLateral: event.target.checked,
                    ...(!event.target.checked ? { config: { ...exercise.config, trackPerSide: false } } : {}),
                  })}
                  name="unilateral"
                />
              }
              label="Unilateral"
            />
          </Stack>
          <Tooltip title="Copy categories and difficulty from a progression or regression">
            <IconButton aria-label="Copy categories and difficulty" sx={{ color: 'common.white' }} onClick={() => props.openDialog('copyCategories')}>
              <ContentCopy />
            </IconButton>
          </Tooltip>
        </Stack>
        <ExerciseCategoryAutocomplete
          selectedCategoryIds={exercise.categoryIds}
          setSelectedCategoryIds={(ids) => {
            updateExercise({ categoryIds: ids });
          }}
        />
        <TextField
          label={'Difficulty'}
          fullWidth
          value={exercise.difficulty ?? ''}
          onChange={(e: any) => {
            updateExercise({ difficulty: !isNaN(Number(e.target.value)) ? Number(e.target.value) : undefined });
          }}
        />
      </Stack>
    </ExerciseEditSection>
  );
};

export const ExerciseEquipmentSection = (props: SectionProps & { openDialog: (dialog: EditorDialog) => void }) => {
  const { updateExercise } = props.editor;
  const { exercise } = props;

  return (
    <ExerciseEditSection title="Equipment">
      <Stack gap={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <EquipmentAutocomplete
              selectedEquipment={exercise.equipment.filter((e) => !e.optional)}
              setSelectedEquipment={(ec) => {
                const optional = exercise.equipment.filter((e) => e.optional);
                const fixed = ec;
                updateExercise({ equipment: [...fixed, ...optional] });
              }}
            />
          </Box>
          <Tooltip title="Copy equipment from a progression or regression">
            <IconButton aria-label="Copy equipment" sx={{ color: 'common.white', flexShrink: 0 }} onClick={() => props.openDialog('copyEquipment')}>
              <ContentCopy />
            </IconButton>
          </Tooltip>
        </Stack>
        <EquipmentAutocomplete
          optional
          selectedEquipment={exercise.equipment.filter((e) => e.optional)}
          setSelectedEquipment={(ec) => {
            const optional = ec;
            const fixed = exercise.equipment.filter((e) => !e.optional);
            updateExercise({ equipment: [...fixed, ...optional] });
          }}
        />
      </Stack>
    </ExerciseEditSection>
  );
};

export const ExerciseProgressionsSection = (props: SectionProps & { openDialog: (dialog: EditorDialog) => void }) => {
  const { exercises } = useAllExercisesQuery(true);
  const { updateExercise } = props.editor;
  const { exercise, openDialog } = props;
  return (
    <ExerciseEditSection title="Progressions">
      <Stack sx={{ mt: 1, gap: 0.5, mx: 1 }}>
        <Stack direction="row">
          <IconButton
            aria-label="Add progression"
            onClick={() => {
              openDialog('progression');
            }}
          >
            <AddIcon />
          </IconButton>
        </Stack>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {exercise.progressionExerciseIds.map((id) => (
            <Chip
              key={id}
              onDelete={() => {
                updateExercise({ progressionExerciseIds: exercise.progressionExerciseIds.filter((pid) => pid !== id) });
              }}
              label={exercises?.find((e) => e.id === id)?.name}
              deleteIcon={<RemoveIcon />}
            />
          ))}
        </Stack>
      </Stack>
    </ExerciseEditSection>
  );
};

export const ExerciseRegressionsSection = (props: SectionProps & { openDialog: (dialog: EditorDialog) => void }) => {
  const { updateExercise } = props.editor;
  const { exercise, openDialog } = props;
  const { exercises } = useAllExercisesQuery(true);
  return (
    <ExerciseEditSection title="Regressions">
      <Stack sx={{ mt: 1, gap: 0.5, mx: 1 }}>
        <Stack direction="row">
          <IconButton
            aria-label="Add regression"
            onClick={() => {
              openDialog('regression');
            }}
          >
            <AddIcon />
          </IconButton>
        </Stack>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {exercise.regressionExerciseIds.map((id) => (
            <Chip
              key={id}
              onDelete={() => {
                updateExercise({ regressionExerciseIds: exercise.regressionExerciseIds.filter((pid) => pid !== id) });
              }}
              label={exercises?.find((e) => e.id === id)?.name}
              deleteIcon={<RemoveIcon />}
            />
          ))}
        </Stack>
      </Stack>
    </ExerciseEditSection>
  );
};

export const ExerciseSupportExercisesSection = (props: SectionProps & { openDialog: (dialog: EditorDialog) => void; editSupportExercise: (value: SupportExerciseDTO) => void }) => {
  const { updateExercise } = props.editor;
  const { exercise, openDialog } = props;
  const { exercises } = useAllExercisesQuery(true);
  return (
    <ExerciseEditSection title="Support exercises">
      <Stack sx={{ mt: 1, gap: 0.5, mx: 1 }}>
        <Stack direction="row" spacing={0.5}>
          <IconButton
            aria-label="Add support exercise"
            onClick={() => {
              openDialog('support');
            }}
          >
            <AddIcon />
          </IconButton>
          <Tooltip title="Copy support exercises from a progression or regression">
            <IconButton aria-label="Copy support exercises" onClick={() => openDialog('copySupport')}>
              <ContentCopy />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {exercise.supportExercises.map((supportExercise) => (
            <Tooltip key={supportExercise.exerciseId} title={supportExercise.description || 'No description'} arrow>
              <Chip
                aria-label={`Edit ${exercises?.find((candidate) => candidate.id === supportExercise.exerciseId)?.name ?? 'support exercise'}`}
                icon={<EditIcon />}
                onClick={() => props.editSupportExercise(supportExercise)}
                onDelete={(event) => {
                  event.stopPropagation();
                  updateExercise({ supportExercises: exercise.supportExercises.filter((value) => value.exerciseId !== supportExercise.exerciseId) });
                }}
                label={exercises?.find((candidate) => candidate.id === supportExercise.exerciseId)?.name}
                deleteIcon={<RemoveIcon />}
              />
            </Tooltip>
          ))}
        </Stack>
      </Stack>
    </ExerciseEditSection>
  );
};

const prerequisiteSummary = (prerequisite: ExercisePrerequisiteDTO) => {
  const values = [
    prerequisite.minSets !== undefined ? `${prerequisite.minSets} sets` : undefined,
    prerequisite.minReps !== undefined ? `${prerequisite.minReps} reps` : undefined,
    prerequisite.minDurationSeconds !== undefined ? `${prerequisite.minDurationSeconds} sec` : undefined,
    prerequisite.minLoadKg !== undefined ? `${prerequisite.minLoadKg} kg` : undefined,
  ];
  return values.filter(Boolean).join(' · ');
};

export const ExercisePrerequisitesSection = (props: SectionProps & { openDialog: (dialog: EditorDialog) => void; editPrerequisite: (value: ExercisePrerequisiteDTO) => void }) => {
  const { updateExercise } = props.editor;
  const { exercise, openDialog } = props;
  const { exercises } = useAllExercisesQuery(true);

  return (
    <ExerciseEditSection title="Prerequisites">
      <Stack spacing={1}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Add prerequisite">
            <IconButton aria-label="Add prerequisite" sx={{ color: 'common.white' }} onClick={() => openDialog('prerequisite')}>
              <AddIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy prerequisites from a progression or regression">
            <IconButton aria-label="Copy prerequisites" sx={{ color: 'common.white' }} onClick={() => openDialog('copyPrerequisites')}>
              <ContentCopy />
            </IconButton>
          </Tooltip>
        </Stack>
        {exercise.prerequisites.map((prerequisite) => (
          <Stack key={prerequisite.exerciseId} direction="row" spacing={1} alignItems="center">
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography noWrap>{exercises?.find((candidate) => candidate.id === prerequisite.exerciseId)?.name ?? prerequisite.exerciseId}</Typography>
              <Typography variant="body2" color="text.secondary">
                {prerequisiteSummary(prerequisite)}
              </Typography>
            </Box>
            <IconButton aria-label="Edit prerequisite" onClick={() => props.editPrerequisite(prerequisite)}>
              <EditIcon />
            </IconButton>
            <IconButton
              aria-label="Remove prerequisite"
              onClick={() => updateExercise({ prerequisites: exercise.prerequisites.filter((value) => value.exerciseId !== prerequisite.exerciseId) })}
            >
              <RemoveIcon />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    </ExerciseEditSection>
  );
};
const ExerciseEditSection = ({ title, children }: PropsWithChildren<{ title: string }>) => (
  <Paper elevation={8} sx={{ ...exerciseDetailsPaperListStyle, height: '100%', minWidth: 0 }}>
    <PaperSubheader title={title} />
    <Box sx={{ p: 2, '& .MuiAutocomplete-root': { minWidth: 0, width: '100%' } }}>{children}</Box>
  </Paper>
);
